import { GetUserResponseDto } from "@/dto/get-user-response.dto";
import { api } from "@/lib/api";
import { UserDTO } from "./authService";

export interface InviteRequest {
  email: string;
  role: string;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  companyName: string;
}

export interface UpdateUserPasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateUserResponse {
  user: UserDTO;
}

export interface UpdateUserPasswordResponse {
  message: string;
}

export interface InviteResponse {
  id: string;
  email: string;
  role: string;
}

export interface AcceptInviteResponse {
  message: string;
  invitationId: string;
}

export interface DeleteUserResponse {
  message: string;
}

export interface GetUsersResponse {
  users: GetUserResponseDto[];
}

export interface ValidateInviteResponse {
  invitationId: string;
  email: string;
  role: string;
}

export interface GetGlobalInvitationsResponse {
  _id: string;
  email: string;
  role: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  invitedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface BulkUserValidationResult {
  email: string;
  exists: boolean;
  user?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    companyName?: string;
  };
  projectMemberships: string[]; // Array of project IDs where user is already a member
  pendingInvitations: string[]; // Array of project IDs where user has pending invitations
  error?: string;
}

export interface BulkValidateUsersRequest {
  emails: string[];
  projectIds?: string[];
}

export interface BulkValidateUsersResponse {
  results: BulkUserValidationResult[];
}

export interface BulkInviteResult {
  email: string;
  success: boolean;
  message: string;
  invitationId?: string;
  error?: string;
}

export interface BulkInviteUsersRequest {
  emails: string[];
  role: string;
  projectIds?: string[];
}

export interface BulkInviteUsersResponse {
  results: BulkInviteResult[];
  successCount: number;
  failureCount: number;
}

export interface DeleteGlobalInvitationResponse {
  message: string;
}

export interface ResendGlobalInvitationResponse {
  message: string;
  invitationId: string;
  expiresAt: string;
  email: string;
  role: string;
}

export const userApi = {
  getUsers: async (): Promise<GetUsersResponse> => {
    const resp = await api.get<GetUsersResponse>("/users");
    return resp.data;
  },
  getUser: async (email: string): Promise<{ user: GetUserResponseDto }> => {
    const resp = await api.get<{ user: GetUserResponseDto }>(
      `/users/by-email?email=${email}`
    );
    return resp.data;
  },
  inviteUser: async (data: InviteRequest): Promise<InviteResponse> => {
    // Use the bulk invite users endpoint for consistency
    const resp = await api.post<BulkInviteUsersResponse>("/users/bulk-invite", {
      emails: [data.email],
      role: data.role,
      projectIds: [], // Empty array for global invitations
    });
    // Return the first result in the expected format
    const result = resp.data.results[0];
    return {
      id: result.invitationId || "",
      email: result.email,
      role: data.role,
    };
  },
  validateInvite: async (token: string) => {
    const response = await api.get(`/unified-invitations/validate/${token}`);
    return {
      invitationId: response.data.invitationId,
      email: response.data.email,
      role: response.data.role,
      invitedBy: response.data.invitedBy,
    };
  },
  acceptInvite: async (token: string): Promise<AcceptInviteResponse> => {
    const resp = await api.post<AcceptInviteResponse>(
      `/unified-invitations/accept`,
      { token }
    );
    return resp.data;
  },
  updateUser: async (
    id: string,
    data: UpdateUserRequest
  ): Promise<UpdateUserResponse> => {
    const resp = await api.put(`/users/${id}`, data);
    return resp.data;
  },
  updateUserPassword: async (
    id: string,
    data: UpdateUserPasswordRequest
  ): Promise<UpdateUserPasswordResponse> => {
    const resp = await api.put(`/users/me/password/${id}`, data);
    return resp.data;
  },
  deleteUser: async (id: string): Promise<DeleteUserResponse> => {
    const resp = await api.delete(`/users/${id}`);
    return resp.data;
  },
  getGlobalInvitations: async (): Promise<GetGlobalInvitationsResponse[]> => {
    // Use unified invitations endpoint for global invitations
    const resp = await api.get("/unified-invitations?isGlobalInvite=true");
    return resp.data.invitations || [];
  },
  deleteGlobalInvitation: async (inviteId: string): Promise<DeleteGlobalInvitationResponse> => {
    const resp = await api.delete<DeleteGlobalInvitationResponse>(`/unified-invitations/${inviteId}`);
    return resp.data;
  },
  resendGlobalInvitation: async (inviteId: string): Promise<ResendGlobalInvitationResponse> => {
    const resp = await api.post<ResendGlobalInvitationResponse>(`/unified-invitations/${inviteId}/resend`);
    return resp.data;
  },
  bulkValidateUsers: async (data: BulkValidateUsersRequest): Promise<BulkValidateUsersResponse> => {
    const resp = await api.post<BulkValidateUsersResponse>("/users/bulk-validate", data);
    return resp.data;
  },
  bulkInviteUsers: async (data: BulkInviteUsersRequest): Promise<BulkInviteUsersResponse> => {
    const resp = await api.post<BulkInviteUsersResponse>("/users/bulk-invite", data);
    return resp.data;
  },
};
